import React, { useState } from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { acquireDelegatedGraphToken } from '../utils/msalTokenService';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

interface NonPreviewableDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  filePath?: string;
  highlightText?: string;
  pageIndex?: number | null;
  factSourceType?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
}

const MAX_HIGHLIGHT_LENGTH = 200;

function truncateHighlight(highlightText?: string): string | undefined {
  return highlightText && highlightText.length > MAX_HIGHLIGHT_LENGTH
    ? highlightText.slice(0, MAX_HIGHLIGHT_LENGTH) + '…'
    : highlightText;
}

async function openSharePointItem(siteId: string, driveId: string, itemId: string): Promise<void> {
  const graphToken = await acquireDelegatedGraphToken();
  const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${itemId}?$select=webUrl`;
  const res = await fetch(graphUrl, {
    headers: { Authorization: `Bearer ${graphToken}` }
  });
  if (!res.ok) throw new Error(`Graph API returned ${res.status}`);
  const data = await res.json();
  if (data.webUrl) {
    window.open(data.webUrl, '_blank', 'noopener,noreferrer');
  }
}

function DocumentLocationHints({
  displayPage,
  factSourceType,
  truncatedHighlight,
}: {
  displayPage: number | null;
  factSourceType?: string;
  truncatedHighlight?: string;
}) {
  if (displayPage == null && !truncatedHighlight) return null;
  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      {displayPage != null && (
        <p className="text-gray-700">
          The information you're looking for is on{' '}
          <span className="font-semibold">page {displayPage}</span>
          {factSourceType === 'image' ? ' (image content)' : ''}.
        </p>
      )}

      {truncatedHighlight && (
        <div>
          <p className="text-sm text-gray-500 mb-1">Search for:</p>
          <blockquote className="border-l-4 border-cvc-lb1 pl-3 py-1 text-sm text-gray-600 italic">
            &ldquo;{truncatedHighlight}&rdquo;
          </blockquote>
        </div>
      )}
    </div>
  );
}

function DocumentModalHeader({ documentName, onClose }: { documentName: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
      <h2 id="non-previewable-document-modal-title" className="text-lg font-semibold text-gray-900 truncate pr-4">
        {documentName}
      </h2>
      <button
        onClick={onClose}
        className="p-2 rounded-lg hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-cvc-blue transition-colors shrink-0"
        aria-label="Close modal"
      >
        <XMarkIcon className="h-5 w-5 text-gray-500" />
      </button>
    </div>
  );
}

function OpenDocumentButton({ isOpening, onOpen }: { isOpening: boolean; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      disabled={isOpening}
      className="inline-flex items-center gap-2 px-4 py-2 bg-cvc-blue text-white rounded-lg hover:bg-cvc-blue/90 focus:outline-hidden focus:ring-2 focus:ring-cvc-blue transition-colors disabled:opacity-60"
    >
      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      {isOpening ? 'Opening…' : 'Open document'}
    </button>
  );
}

function useOpenDocument(
  filePath?: string,
  sharepointSiteId?: string,
  sharepointDriveId?: string,
  sharepointItemId?: string,
) {
  const [opening, setOpening] = useState(false);

  const hasSharePointIds = !!(sharepointSiteId && sharepointDriveId && sharepointItemId);
  const hasDirectUrl = !!(filePath && /^https?:\/\//i.test(filePath));
  const canOpen = hasDirectUrl || hasSharePointIds;

  const openDocument = async () => {
    if (hasDirectUrl) {
      window.open(filePath, '_blank', 'noopener,noreferrer');
      return;
    }

    if (hasSharePointIds) {
      setOpening(true);
      try {
        await openSharePointItem(sharepointSiteId, sharepointDriveId, sharepointItemId);
      } catch (err) {
        console.error('Failed to get SharePoint web URL:', err);
      } finally {
        setOpening(false);
      }
    }
  };

  return { canOpen, opening, openDocument };
}

export default function NonPreviewableDocumentModal({
  isOpen,
  onClose,
  documentName,
  filePath,
  highlightText,
  pageIndex,
  factSourceType,
  sharepointSiteId,
  sharepointDriveId,
  sharepointItemId,
}: NonPreviewableDocumentModalProps) {
  const { canOpen, opening, openDocument } = useOpenDocument(
    filePath,
    sharepointSiteId,
    sharepointDriveId,
    sharepointItemId,
  );

  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  const displayPage = pageIndex != null ? pageIndex + 1 : null;

  const truncatedHighlight = truncateHighlight(highlightText);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="non-previewable-document-modal-title"
          className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          <DocumentModalHeader documentName={documentName} onClose={onClose} />

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <p className="text-gray-700">
              This document type cannot be previewed within DealOS.
            </p>

            <DocumentLocationHints
              displayPage={displayPage}
              factSourceType={factSourceType}
              truncatedHighlight={truncatedHighlight}
            />

            {canOpen && <OpenDocumentButton isOpening={opening} onOpen={() => void openDocument()} />}
          </div>
        </div>
      </div>
    </>
  );
}
